import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  CheckCircleOutlined as AcceptIcon,
  LockOpenOutlined as ReleaseIcon,
  SwapHorizOutlined as TransferIcon,
  CloseOutlined as CloseIcon,
  LockOpen as ReopenIcon,
  VisibilityOutlined as VisibilityIcon,
  SearchOutlined as ViewContactIcon,
  PushPinOutlined as PinIcon,
  PushPin as UnpinIcon,
  FavoriteBorder as FavoriteIcon,
  PlaylistAddOutlined as ListsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import HeartOffIcon from './HeartOffIcon';

const ACTION_COLOR = {
  viewContact: 'primary',
  pin: 'leadPin',
  unpin: 'leadPin',
  favorite: 'leadFavorite',
  unfavorite: 'leadFavorite',
  lists: 'primary',
  accept: 'leadAccept',
  preview: 'primary',
  history: 'leadHistory',
  transfer: 'leadTransfer',
  release: 'leadRelease',
  close: 'error',
  reopen: 'leadAccept',
};

const OUTLINED_ACTIONS = ['transfer', 'release'];

// Ações do lead, dirigidas pelas capabilities que o backend já serializa em
// `authorization.capabilities`. Uma ação só aparece quando a capability permite E
// a tela passa o handler correspondente — assim a prévia reaproveita o mesmo
// componente omitindo apenas `onPreview`.
//
// variant="menu"    → botão de três pontos (padrão: cards da lista, telas estreitas)
// variant="inline"  → cápsula com os ícones coloridos lado a lado
// variant="buttons" → botões completos, com rótulo, ocupando a largura disponível
const LeadActionsMenu = ({
  lead,
  pendingAction,
  disabled = false,
  variant = 'menu',
  onAccept,
  onRelease,
  onTransfer,
  onClose,
  onReopen,
  onPreview,
  onPin,
  onUnpin,
  onFavorite,
  onUnfavorite,
  onManageLists,
  onViewContact,
  iconSize = 'small',
  coloredIcons = true,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const actions = [
    { key: 'viewContact', allowed: lead.canViewContact, labelKey: 'leads.details', icon: <ViewContactIcon fontSize="small" />, run: onViewContact },
    { key: 'accept', allowed: lead.canAccept, labelKey: 'leads.accept', icon: <AcceptIcon fontSize="small" />, run: onAccept },
    { key: 'preview', allowed: lead.canPreview, labelKey: 'leads.preview', icon: <VisibilityIcon fontSize="small" />, run: onPreview },
    { key: 'history', allowed: lead.canViewHistory, labelKey: 'leads.view_history', icon: <VisibilityIcon fontSize="small" />, run: onPreview },
    { key: 'transfer', allowed: lead.canTransfer, labelKey: 'leads.transfer', icon: <TransferIcon fontSize="small" />, run: onTransfer },
    { key: 'release', allowed: lead.canRelease, labelKey: 'leads.release', icon: <ReleaseIcon fontSize="small" />, run: onRelease },
    { key: 'close', allowed: lead.canClose, labelKey: 'leads.close', icon: <CloseIcon fontSize="small" />, run: onClose },
    { key: 'reopen', allowed: lead.canReopen, labelKey: 'leads.reopen', icon: <ReopenIcon fontSize="small" />, run: onReopen },
    { key: 'unpin', allowed: lead.isPinned, labelKey: 'leads.unpin', icon: <UnpinIcon fontSize="small" />, run: onUnpin },
    { key: 'pin', allowed: !lead.isPinned, labelKey: 'leads.pin', icon: <PinIcon fontSize="small" />, run: onPin },
    { key: 'unfavorite', allowed: lead.isFavorite, labelKey: 'leads.unfavorite', icon: <HeartOffIcon fontSize="small" />, run: onUnfavorite },
    { key: 'favorite', allowed: !lead.isFavorite, labelKey: 'leads.favorite', icon: <FavoriteIcon fontSize="small" />, run: onFavorite },
    { key: 'lists', allowed: true, labelKey: 'leads.lists.addToList', icon: <ListsIcon fontSize="small" />, run: onManageLists },
  ].filter((action) => action.allowed && action.run);

  if (actions.length === 0) return null;

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handlePick = (run) => (e) => {
    handleClose(e);
    run();
  };

  const colorOf = (action) => ACTION_COLOR[action.key];

  const iconFor = (action) => (
    pendingAction === action.key
      ? <CircularProgress size={20} color="inherit" />
      : action.icon
  );

  if (variant === 'buttons') {
    return (
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {actions.map((action) => (
          <Button
            key={action.key}
            fullWidth
            color={colorOf(action)}
            variant={OUTLINED_ACTIONS.includes(action.key) ? 'outlined' : 'contained'}
            disabled={disabled}
            startIcon={iconFor(action)}
            onClick={(e) => { e.stopPropagation(); action.run(); }}
          >
            {t(action.labelKey)}
          </Button>
        ))}
      </Stack>
    );
  }

  if (variant === 'inline') {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 5 }}>
        <Stack direction="row" spacing={0.5} px={0.5}>
          {actions.map((action) => (
            <Tooltip key={action.key} title={t(action.labelKey)}>
              {/* span: Tooltip precisa de um filho habilitado para receber o hover */}
              <span>
                <IconButton
                  size="small"
                  aria-label={t(action.labelKey)}
                  disabled={disabled}
                  sx={{ color: `${colorOf(action)}.main` }}
                  onClick={(e) => { e.stopPropagation(); action.run(); }}
                >
                  {iconFor(action)}
                </IconButton>
              </span>
            </Tooltip>
          ))}
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <IconButton
        size={iconSize}
        aria-label={t('leads.actions')}
        disabled={disabled}
        onClick={handleOpen}
      >
        <MoreVertIcon fontSize={iconSize} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        {actions.map((action) => (
          <MenuItem key={action.key} disabled={disabled} onClick={handlePick(action.run)}>
            <ListItemIcon sx={coloredIcons ? { color: `${colorOf(action)}.main` } : undefined}>
              {iconFor(action)}
            </ListItemIcon>
            <ListItemText>{t(action.labelKey)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LeadActionsMenu;
